// Declarative React SVG renderer for one room "group" (tree of tables,
// seats, decorations). Replaces the previous imperative implementation that
// called `document.createElementNS` and attached DOM event listeners by
// hand.
//
// Seat <circle> nodes register themselves in `seatRegistry` via callback
// refs so that the diff-based painter in `applySeatStates` can keep mutating
// classes per-element without re-rendering. Same goes for tables: the table
// <g> and the optional round-table <circle> register in `tableRegistry`.

import React, { useRef } from 'react';
import type {
  Group,
  GroupChild,
  PictureChild,
  RectChild,
  SeatChild,
  SeatNode,
  ShapeChild,
  TableChild,
  TableSegment,
  TextChild,
} from '../types';
import {
  seatRegistry,
  tableRegistry,
} from '../utils/updateSeatStates';

interface GroupRendererProps {
  group: Group;
  handleTableClick?: (table: TableChild) => void;
  handleSeatClick?: (seat: SeatNode | SeatChild) => void;
  handleMouseEnter?: (e: React.MouseEvent, element: GroupChild | SeatNode) => void;
  handleMouseMove?: (e: React.MouseEvent) => void;
  handleMouseLeave?: (e: React.MouseEvent) => void;
  parseARGBToRGBA?: (argb: string) => string;
}

const GroupRendererImpl: React.FC<GroupRendererProps> = ({
  group,
  handleTableClick,
  handleSeatClick,
  handleMouseEnter,
  handleMouseMove,
  handleMouseLeave,
  parseARGBToRGBA,
}) => {
  // Note: seat-state changes (seat colors, table-occupied) are applied by
  // ``applySeatStates`` in the parent ``RoomSVG`` via the seat/table
  // registries that callback refs populate below. This component therefore
  // does not depend on ``seatToGuestMap`` and re-renders only when its
  // ``group`` prop reference changes — i.e. essentially never after mount.

  return (
    <g>
      {group.children.map((child, idx) => {
        switch (child.type) {
          case 'table':
            return (
              <TableNode
                key={(child as TableChild).guid || idx}
                table={child as TableChild}
                onTableClick={handleTableClick}
                onSeatClick={handleSeatClick}
                onMouseEnter={handleMouseEnter}
                onMouseMove={handleMouseMove}
                onMouseLeave={handleMouseLeave}
              />
            );
          case 'seat':
            return (
              <LooseSeatNode
                key={(child as SeatChild).guid || idx}
                seat={child as SeatChild}
                onSeatClick={handleSeatClick}
                onMouseEnter={handleMouseEnter}
                onMouseMove={handleMouseMove}
                onMouseLeave={handleMouseLeave}
              />
            );
          case 'picture':
            return <PictureNode key={idx} pic={child as PictureChild} />;
          case 'rect':
            return (
              <RectNode
                key={idx}
                rect={child as RectChild}
                parseARGBToRGBA={parseARGBToRGBA}
              />
            );
          case 'shape':
            return (
              <ShapeNode
                key={idx}
                shape={child as ShapeChild}
                parseARGBToRGBA={parseARGBToRGBA}
              />
            );
          case 'text':
            return (
              <TextNode
                key={idx}
                text={child as TextChild}
                parseARGBToRGBA={parseARGBToRGBA}
              />
            );
          default:
            return null;
        }
      })}
    </g>
  );
};

// Memoize: the only prop that meaningfully changes after mount is ``group``
// (and only when the room layout itself reloads). Event handlers from
// ``RoomSVG`` are stable enough; in practice the parent recreates them on
// every render, so we shallow-compare by ``group`` reference only.
const GroupRenderer = React.memo(
  GroupRendererImpl,
  (prev, next) => prev.group === next.group,
);

export default GroupRenderer;

// ---------------------------------------------------------------------------
// TableNode
// ---------------------------------------------------------------------------

interface TableNodeProps {
  table: TableChild;
  onTableClick?: (table: TableChild) => void;
  onSeatClick?: (seat: SeatNode | SeatChild) => void;
  onMouseEnter?: (e: React.MouseEvent, element: GroupChild | SeatNode) => void;
  onMouseMove?: (e: React.MouseEvent) => void;
  onMouseLeave?: (e: React.MouseEvent) => void;
}

const TableNode: React.FC<TableNodeProps> = ({
  table,
  onTableClick,
  onSeatClick,
  onMouseEnter,
  onMouseMove,
  onMouseLeave,
}) => {
  const tableGuid = table.guid || table.tableId || table.id || '';
  const tableLabel =
    table.tableId || table.label || table.name || table.table || tableGuid;

  // Mutable holder so the round-segment ref can attach the segment element to
  // the same registry entry the table <g> created.
  const entryRef = useRef<{
    tableGroupEl?: SVGGElement;
    segmentEl?: SVGCircleElement;
    seats: Set<string>;
  }>({ seats: new Set() });

  const setTableGroupRef = (el: SVGGElement | null) => {
    if (el) {
      entryRef.current.tableGroupEl = el;
      tableRegistry.set(tableGuid, {
        tableGroupEl: el,
        segmentEl: entryRef.current.segmentEl,
        seats: entryRef.current.seats,
      });
    } else {
      tableRegistry.delete(tableGuid);
    }
  };

  const setRoundSegmentRef = (el: SVGCircleElement | null) => {
    entryRef.current.segmentEl = el || undefined;
    const existing = tableRegistry.get(tableGuid);
    if (existing) {
      existing.segmentEl = el || undefined;
    }
  };

  const transform =
    `translate(${table.left + table.width / 2}, ${table.top + table.height / 2}) ` +
    `rotate(${table.angle || 0}) ` +
    `translate(${-table.width / 2}, ${-table.height / 2})`;

  return (
    <g
      ref={setTableGroupRef}
      data-table-guid={tableGuid}
      role="group"
      aria-label={`Tisch ${tableLabel}`}
      transform={transform}
    >
      <title>{`Tisch ${tableLabel}`}</title>

      {table['table-segments'].map((segment, i) => (
        <TableSegmentNode
          key={i}
          segment={segment}
          tableGuid={tableGuid}
          segmentTypePrefix={table['segment-type'] ?? ''}
          setRoundSegmentRef={setRoundSegmentRef}
        />
      ))}

      {table.seats?.map((seat) => (
        <SeatInTableNode
          key={seat.guid}
          seat={seat}
          tableGuid={tableGuid}
          tableLabel={String(tableLabel)}
          tableSeats={entryRef.current.seats}
          onSeatClick={onSeatClick}
          onMouseEnter={onMouseEnter}
          onMouseMove={onMouseMove}
          onMouseLeave={onMouseLeave}
        />
      ))}

      {tableLabel ? (
        <text
          x={table.width / 2}
          y={table.height / 2}
          textAnchor="middle"
          dominantBaseline="middle"
          fontSize={20}
          fill="#222"
          pointerEvents="none"
          style={{ fontWeight: 'bold', userSelect: 'none', WebkitUserSelect: 'none' }}
        >
          {String(tableLabel)}
        </text>
      ) : null}

      {/* Transparent click-rect over the table area */}
      <rect
        x={0}
        y={0}
        width={table.width}
        height={table.height}
        fill="transparent"
        pointerEvents="all"
        style={{ cursor: 'pointer' }}
        onClick={(e) => {
          e.stopPropagation();
          onTableClick?.(table);
        }}
      />
    </g>
  );
};

// ---------------------------------------------------------------------------
// TableSegmentNode
// ---------------------------------------------------------------------------

interface TableSegmentNodeProps {
  segment: TableSegment;
  tableGuid: string;
  segmentTypePrefix: string;
  setRoundSegmentRef: (el: SVGCircleElement | null) => void;
}

const TableSegmentNode: React.FC<TableSegmentNodeProps> = ({
  segment,
  tableGuid,
  segmentTypePrefix,
  setRoundSegmentRef,
}) => {
  if (segment.drawtype === 'sdtImage') {
    const segType = segment.type ?? '';
    const typeSubstring =
      segType.length > 2 ? segType.substring(2).toLowerCase() : segType.toLowerCase();
    if (typeSubstring === '_csc') {
      return (
        <circle
          cx={segment.left + 30}
          cy={segment.top + 30}
          r={21}
          stroke="#808080"
          strokeWidth={2}
          fill="white"
        />
      );
    }
    const imageFilename = `${segmentTypePrefix}${typeSubstring}.png`;
    return (
      <image
        href={`./images/tables/${imageFilename}`}
        x={segment.left}
        y={segment.top}
        width={segment.width}
        height={segment.height}
        preserveAspectRatio="none"
      />
    );
  }
  if (segment.drawtype === 'sdtRoundTable') {
    const cx = segment.left + segment.width / 2;
    const cy = segment.top + segment.height / 2;
    const radius = Math.min(segment.width, segment.height) / 2;
    return (
      <circle
        ref={setRoundSegmentRef}
        cx={cx}
        cy={cy}
        r={radius}
        fill="#fff"
        stroke="#808080"
        strokeWidth="1px"
        data-table-guid={tableGuid}
        data-segment="sdtRoundTable"
      />
    );
  }
  return (
    <rect
      x={segment.left}
      y={segment.top}
      width={segment.width}
      height={segment.height}
      fill="#ccc"
      stroke="#000"
    />
  );
};

// ---------------------------------------------------------------------------
// SeatInTableNode
// ---------------------------------------------------------------------------

interface SeatInTableNodeProps {
  seat: SeatNode;
  tableGuid: string;
  tableLabel: string;
  tableSeats: Set<string>;
  onSeatClick?: (seat: SeatNode | SeatChild) => void;
  onMouseEnter?: (e: React.MouseEvent, element: GroupChild | SeatNode) => void;
  onMouseMove?: (e: React.MouseEvent) => void;
  onMouseLeave?: (e: React.MouseEvent) => void;
}

const SeatInTableNode: React.FC<SeatInTableNodeProps> = ({
  seat,
  tableGuid,
  tableLabel,
  tableSeats,
  onSeatClick,
  onMouseEnter,
  onMouseMove,
  onMouseLeave,
}) => {
  const cx = seat.left + seat.width / 2;
  const cy = seat.top + seat.height / 2;
  const seatNo = seat['seat-index'] !== undefined ? seat['seat-index'] + 1 : '';
  const ariaLabel =
    seatNo !== '' ? `Tisch ${tableLabel}, Sitz ${seatNo}` : `Tisch ${tableLabel}`;

  const setSeatRef = (el: SVGCircleElement | null) => {
    if (el) {
      seatRegistry.set(seat.guid, el);
      tableSeats.add(seat.guid);
    } else {
      seatRegistry.delete(seat.guid);
      tableSeats.delete(seat.guid);
    }
  };

  return (
    <>
      <circle
        ref={setSeatRef}
        cx={cx}
        cy={cy}
        r={20}
        fill="transparent"
        stroke="#000"
        strokeWidth={0}
        id={seat.guid}
        data-table-guid={tableGuid}
        role="button"
        tabIndex={0}
        aria-label={ariaLabel}
        aria-pressed="false"
        onClick={(e) => {
          e.stopPropagation();
          onSeatClick?.(seat);
        }}
        onMouseEnter={(e) => onMouseEnter?.(e, seat)}
        onMouseMove={(e) => onMouseMove?.(e)}
        onMouseLeave={(e) => onMouseLeave?.(e)}
      />
      {seatNo !== '' ? (
        <text
          x={cx}
          y={cy + 5}
          textAnchor="middle"
          fontSize={16}
          fill="#333"
          pointerEvents="none"
          style={{ userSelect: 'none', WebkitUserSelect: 'none' }}
        >
          {String(seatNo)}
        </text>
      ) : null}
    </>
  );
};

// ---------------------------------------------------------------------------
// LooseSeatNode (rare: a seat outside a table)
// ---------------------------------------------------------------------------

interface LooseSeatNodeProps {
  seat: SeatChild;
  onSeatClick?: (seat: SeatNode | SeatChild) => void;
  onMouseEnter?: (e: React.MouseEvent, element: GroupChild | SeatNode) => void;
  onMouseMove?: (e: React.MouseEvent) => void;
  onMouseLeave?: (e: React.MouseEvent) => void;
}

const LooseSeatNode: React.FC<LooseSeatNodeProps> = ({
  seat,
  onSeatClick,
  onMouseEnter,
  onMouseMove,
  onMouseLeave,
}) => {
  const cx = seat.left + seat.width / 2;
  const cy = seat.top + seat.height / 2;

  const setSeatRef = (el: SVGCircleElement | null) => {
    if (el) {
      seatRegistry.set(seat.guid, el);
    } else {
      seatRegistry.delete(seat.guid);
    }
  };

  return (
    <circle
      ref={setSeatRef}
      cx={cx}
      cy={cy}
      r={15}
      fill="transparent"
      stroke="#000"
      strokeWidth={1}
      id={seat.guid}
      data-table-guid={seat.tableGuid || undefined}
      onClick={(e) => {
        e.stopPropagation();
        onSeatClick?.(seat);
      }}
      onMouseEnter={(e) => onMouseEnter?.(e, seat)}
      onMouseMove={(e) => onMouseMove?.(e)}
      onMouseLeave={(e) => onMouseLeave?.(e)}
    />
  );
};

// ---------------------------------------------------------------------------
// PictureNode
// ---------------------------------------------------------------------------

const PictureNode: React.FC<{ pic: PictureChild }> = ({ pic }) => {
  const transform = pic.angle
    ? `rotate(${pic.angle}, ${pic.left + pic.width / 2}, ${pic.top + pic.height / 2})`
    : undefined;
  return (
    <image
      href={`./images/objects/${pic.filename}`}
      x={pic.left}
      y={pic.top}
      width={pic.width}
      height={pic.height}
      preserveAspectRatio="none"
      transform={transform}
    />
  );
};

// ---------------------------------------------------------------------------
// RectNode
// ---------------------------------------------------------------------------

const RectNode: React.FC<{
  rect: RectChild;
  parseARGBToRGBA?: (argb: string) => string;
}> = ({ rect, parseARGBToRGBA }) => (
  <rect
    x={rect.left}
    y={rect.top}
    width={rect.width}
    height={rect.height}
    fill={
      parseARGBToRGBA && rect.fillcolor
        ? parseARGBToRGBA(rect.fillcolor)
        : rect.fillcolor || '#ccc'
    }
    stroke={
      parseARGBToRGBA && rect.strokecolor
        ? parseARGBToRGBA(rect.strokecolor)
        : rect.strokecolor || '#000'
    }
    strokeWidth={1}
  />
);

// ---------------------------------------------------------------------------
// ShapeNode
// ---------------------------------------------------------------------------

const ShapeNode: React.FC<{
  shape: ShapeChild;
  parseARGBToRGBA?: (argb: string) => string;
}> = ({ shape, parseARGBToRGBA }) => {
  const fill =
    parseARGBToRGBA && shape.fillcolor
      ? parseARGBToRGBA(shape.fillcolor)
      : shape.fillcolor || '#ccc';
  const stroke =
    parseARGBToRGBA && shape.strokecolor
      ? parseARGBToRGBA(shape.strokecolor)
      : shape.strokecolor || '#000';

  if (shape.drawtype === 'rect') {
    return (
      <rect
        x={shape.left}
        y={shape.top}
        width={shape.width}
        height={shape.height}
        fill={fill}
        stroke={stroke}
        strokeWidth={1}
      />
    );
  }
  if (shape.drawtype === 'circle') {
    const cx = shape.left + shape.width / 2;
    const cy = shape.top + shape.height / 2;
    return (
      <circle
        cx={cx}
        cy={cy}
        r={Math.min(shape.width, shape.height) / 2}
        fill={fill}
        stroke={stroke}
        strokeWidth={1}
      />
    );
  }
  if (shape.drawtype === 'ellipse') {
    const cx = shape.left + shape.width / 2;
    const cy = shape.top + shape.height / 2;
    return (
      <ellipse
        cx={cx}
        cy={cy}
        rx={shape.width / 2}
        ry={shape.height / 2}
        fill={fill}
        stroke={stroke}
        strokeWidth={1}
      />
    );
  }
  return null;
};

// ---------------------------------------------------------------------------
// TextNode
// ---------------------------------------------------------------------------

const TextNode: React.FC<{
  text: TextChild;
  parseARGBToRGBA?: (argb: string) => string;
}> = ({ text, parseARGBToRGBA }) => {
  const textAnchor =
    text.align === 'center' ? 'middle' : text.align === 'right' ? 'end' : 'start';
  return (
    <text
      x={text.left + 20}
      y={text.top + (text.fontsize ? text.fontsize : 16)}
      fontSize={text.fontsize ? text.fontsize * 1.2 : 16}
      fill={
        parseARGBToRGBA && text.fontcolor
          ? parseARGBToRGBA(text.fontcolor)
          : text.fontcolor || '#000'
      }
      pointerEvents="none"
      fontFamily={text.font}
      textAnchor={textAnchor}
      dominantBaseline="baseline"
    >
      {text.text || ''}
    </text>
  );
};
